import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import storeUserFeedback from 'actions/userFeedback/store';
import { HomeContext } from 'screens/Home/Home';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import { HomeworkContext } from 'screens/Homework/Homework';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { initialPulse, pulse } from 'utilities/framerAnimations';
import Button from 'components/UI/Button/Button';
import { getDeviceInfo } from 'utilities/getDeviceInfo';
import { useDispatch, useSelector } from 'react-redux';
import { MenuContext } from '../../Menu';
import Popup from '../Popup/Popup';
import './Exclamation.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faPaperPlane,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // Old Code
    // write the ArrayBuffer to a blob, and you're done
    // var bb = new BlobBuilder();
    // bb.append(ab);
    // return bb.getBlob(mimeString);

    // New Code
    return new Blob([ab], { type: mimeString });
}

export default function Exclamation() {
    const location = useLocation();
    const routeParams = useParams();
    const ref = useRef(null);
    const dispatch = useDispatch();
    const deviceInfo = getDeviceInfo();

    const { setWelcomeY, welcomeSequence } = useContext(HomeContext);
    const { sequence } = useContext(HomeworkContext);
    const { select, selected, setMenuPointer } = useContext(MenuContext);
    const [description, setDescription] = useState('');
    const [problemType, setProblemType] = useState('');
    const [sendingFeedback, setSendingFeedback] = useState(false);
    const history = useHistory();

    const user = useSelector((state) => {
        if (!state.authentication?.user) return {};
        const { id, name, role, treatments } = state.authentication?.user;

        const activeTreatment = treatments.find((t) => t?.pivot?.open)?.id || 0;

        return { id, name, role, activeTreatment, treatments };
    });

    useEffect(() => {
        if (welcomeSequence === 'exclamation') {
            setWelcomeY(`${ref?.current?.offsetTop}px`);
        }
    }, [welcomeSequence]);

    const download = (image, { name = 'img', extension = 'png' } = {}) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    const [image, takeScreenshot] = useScreenshot();
    const getImage = () =>
        takeScreenshot(document.getElementById('root').firstChild);

    const sendFeedback = () => {
        if (image) {
            setSendingFeedback(true);
            let parsedLocation = '';
            let treatmentId;
            let exerciseId;

            const file = dataURItoBlob(image);

            if (location.pathname === '/') {
                parsedLocation = 'Startpagina';
            } else if (location.pathname.includes('homework')) {
                treatmentId = routeParams.treatmentId;
                parsedLocation = sequence || 'homework';
                exerciseId = routeParams.homeworkId;
            } else if (location.pathname.includes('audio-exercise')) {
                parsedLocation = 'audio';
                treatmentId = routeParams.treatmentId;
                exerciseId = routeParams.exerciseId;
            } else if (location.pathname.includes('flitsprogramma')) {
                parsedLocation = 'flash';
                treatmentId = routeParams.treatmentId;
                exerciseId = routeParams.exerciseId;
            }

            let data = {
                description,
                url: location.pathname,
                file,
                location: parsedLocation,
                metadata: JSON.stringify(deviceInfo),
                user_meta: JSON.stringify(user),
                problemType,
            };

            if (exerciseId) {
                data = {
                    ...data,
                    exercise_id: exerciseId,
                };
            }
            if (treatmentId) {
                data = {
                    ...data,
                    treatment_id: treatmentId,
                };
            }
            dispatch(storeUserFeedback(data))
                .then((e) => {
                    alert('Melding verstuurd!');
                    select('initial');
                    setSendingFeedback(false);
                })
                .catch((e) => {
                    console.error(e);
                    alert('Het is niet gelukt de melding op te slaan.');
                    setSendingFeedback(false);
                });
            // console.log('Downloading image');
            // download(image, { name: 'lorem-ipsum', extension: 'png' });
        }
    };

    return (
        <div styleName="wrapper" ref={ref}>
            <motion.button
                animate={
                    welcomeSequence === 'exclamation' ? pulse : initialPulse
                }
                initial={initialPulse}
                onClick={(e) => {
                    select('exclamation');
                    setMenuPointer(e);
                    getImage();
                }}
                type="button"
            >
                <img alt="city icon" src="/images/menu/icon-help.svg" />
            </motion.button>
            {selected === 'exclamation' && (
                <Popup>
                    <div styleName="popup">
                        <h3>Technisch probleem melden</h3>
                        {image ? (
                            <div styleName="screenshot-wrapper">
                                <img
                                    styleName="screenshot"
                                    alt="screenshot"
                                    src={image}
                                />
                            </div>
                        ) : null}

                        <p>
                            <strong>Werkt iets niet goed?</strong>
                            <br />
                            Je hebt een probleem ontdekt in Spiek Digitaal.
                            Beschrijf hier wat het probleem is:
                        </p>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Geef een beschrijving van het probleem"
                        />
                        <p>
                            Klik aan waar je denkt dat het probleem mee te maken
                            heeft:
                        </p>
                        <div styleName="radio-options">
                            <div
                                styleName={`option ${
                                    problemType === 'treatment'
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() => setProblemType('treatment')}
                            >
                                Ik snap de opdracht niet
                            </div>
                            <div
                                styleName={`option ${
                                    problemType === 'no-treatment'
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() => setProblemType('no-treatment')}
                            >
                                Er staat geen opdracht klaar
                            </div>
                            <div
                                styleName={`option ${
                                    problemType === 'audio' ? 'selected' : ''
                                }`}
                                onClick={() => setProblemType('audio')}
                            >
                                Vreemde uitspraak
                            </div>
                            <div
                                styleName={`option ${
                                    problemType === 'technical'
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() => setProblemType('technical')}
                            >
                                Technisch probleem
                            </div>
                            <div
                                styleName={`option ${
                                    problemType === 'bug-in-treatment'
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() =>
                                    setProblemType('bug-in-treatment')
                                }
                            >
                                Fout in de behandeling
                            </div>
                            <div
                                styleName={`option ${
                                    problemType === 'no-sound' ? 'selected' : ''
                                }`}
                                onClick={() => setProblemType('no-sound')}
                            >
                                Geen geluid
                            </div>
                        </div>
                        {problemType === 'no-sound' && (
                            <div styleName="warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <p>
                                    Probeer eens op{' '}
                                    <span
                                        onClick={() => {
                                            select('initial');
                                            history.push('/test-pagina');
                                        }}
                                    >
                                        de testpagina
                                    </span>{' '}
                                    of daar je geluid wel werkt.
                                </p>
                            </div>
                        )}
                        <div styleName="buttons">
                            <Button
                                onClick={
                                    sendingFeedback === false && sendFeedback
                                }
                            >
                                Versturen
                                <FontAwesomeIcon
                                    spin={sendFeedback === true}
                                    icon={
                                        sendingFeedback
                                            ? faCircleNotch
                                            : faPaperPlane
                                    }
                                />
                            </Button>
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}
